import { ButtonType, DialogFooterButtonsWidget, DialogPositioningBehavior, DialogProps, DialogType, DialogWidget, HostedStackedElementContainer } from '@tableau/widgets';
import * as React from 'react';
import * as _ from 'underscore';
import { isNullOrUndefined } from 'util';
import { Messages } from '../../../codegen/Localize';
import { BackgroundJobDefinition } from '../../../ts/models/ContentDefinitions';
import { IBackgroundJobInfo } from '../../../ts/resources/BackgroundJobs';
import { GetBackgroundJobExtendedInfoRequest } from '../../../ts/ServerApi/requests/GetBackgroundJobExtendedInfoRequest';
import { IBackgroundJob } from '../../../ts/ServerApi/types/IBackgroundJob';
import { getBackgroundJobExtendedInfoDialog, IBackgroundJobExtendedInfo } from '../../../ts/ServerApi/types/IBackgroundJobExtendedInfo';
import { IStringValuedFilterClause } from '../../../ts/ServerApi/types/IFilterClause';
import { ErrorNotifyingServer } from '../../../ts/services/ErrorNotifyingServer';
import { ReactModalService } from '../../../ts/services/ReactModalService';
import { ToasterService, ToastType } from '../../../ts/services/ToasterService';
import { ServiceLocator } from '../../ServiceLocator';


export interface BackgroundJobExtendedInfoDialogProps {
  backgroundJob: IBackgroundJob;
  data: IBackgroundJobExtendedInfo;
  text: string;
  title: string;
  testId: string;
  isServerPage: boolean;
  close: () => void;
}

export interface BackgroundJobExtendedInfoDialogInstance {
  backgroundJob: IBackgroundJobInfo;
  extendedInfo: IBackgroundJobExtendedInfo;
  close: () => void;
}

// The reference to this immutable array gets overwritten as dialogs are open or closed
let modalInstances: ReadonlyArray<BackgroundJobExtendedInfoDialogInstance> = [];

export class BackgroundJobExtendedInfoDialog extends React.PureComponent<BackgroundJobExtendedInfoDialogProps> {
  public static closeAll() {
    modalInstances.forEach(i => i.close());
    modalInstances = [];
  }

  public static toggle(
    backgroundJob: IBackgroundJobInfo
  ) {
    const existingInstance = modalInstances.find(instance => instance.backgroundJob.jobId === backgroundJob.jobId);
    if (existingInstance) {
      existingInstance.close();
    } else {
      BackgroundJobExtendedInfoDialog.open(backgroundJob);
    }
  }

  private static async open(
    backgroundJob: IBackgroundJobInfo
  ) {
    const server = ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer');
    const request = new GetBackgroundJobExtendedInfoRequest({ backgroundJobId: backgroundJob.jobId + '' });
    const extendedInfo = await server.sendRequest(request);

    if (extendedInfo.errors && extendedInfo.errors.length > 0) {
      const service = ToasterService.instance;
      service.pop({
        text: Messages.backgroundJobsExtendedInfoError(),
        type: ToastType.Error,
      });

      // Failed to open the dialog: bail
      return;
    }

    const siteIdFilter = BackgroundJobDefinition.defaultFilters('').siteId;
    const isServerPage = isNullOrUndefined(siteIdFilter) || (siteIdFilter as IStringValuedFilterClause).value === undefined;
    const hostContainer = new HostedStackedElementContainer();

    const close = () => {
      hostContainer && hostContainer.remove();
      modalInstances = _.without(modalInstances, instance);
    };

    const props: BackgroundJobExtendedInfoDialogProps = {
      text: backgroundJob.jobId + '',
      title: Messages.backgroundJobsExtendedInfoTitle({ ID: backgroundJob.jobId + '' }),
      testId: 'background-job-extended-info-' + backgroundJob.jobId,
      isServerPage,
      close,
      backgroundJob: backgroundJob,
      data: extendedInfo,
    };
    const dialog = React.createElement(BackgroundJobExtendedInfoDialog, props);
    hostContainer.render(dialog);

    const instance: BackgroundJobExtendedInfoDialogInstance = {
      backgroundJob, extendedInfo, close,
    };
    modalInstances = modalInstances.concat(instance);
    ReactModalService.registerModal(close);
  }


  constructor(props: BackgroundJobExtendedInfoDialogProps) {
    super(props);
    this.state = {};
  }

  render() {
    const dialogProps: DialogProps = {
      type: DialogType.NormalWithTitleBar,
      title: this.props.title,
      testId: this.props.testId,
      shouldReturnFocusOnClose: false,
      dialogWidth: 375,
      isModal: false,
      showCloseButton: true,
      onCloseRequest: this.props.close,
      positioningBehavior: DialogPositioningBehavior.Draggable,
      showFooter: true,
      footerContent: <DialogFooterButtonsWidget
        buttons={[{
          handleClick: () => {
            this.props.close();
          },
          buttonType: ButtonType.Go,
          children: Messages.backgroundJobsExtendedInfoOK(),
          testId: 'footer-confirm'
        }]}
        testId="footer"
      />,
    };
    return <DialogWidget {...dialogProps}>
        {getBackgroundJobExtendedInfoDialog(this.props.backgroundJob, this.props.data, this.props.isServerPage).map((line, i) =>
          <p
            key={'background-job-extended-info-dialog-line-' + i}
            style={{ margin: '0' }}
          >
            {line}
          </p>
        )}
      </DialogWidget>;
  }
}
